import { useCallback, useEffect, useId, useRef } from "react";

import { useNotificationMuteStore } from "./use-notification-mute-store";

export function useNotificationMuteActivity(
  active: boolean,
  source: string,
): void {
  const reactId = useId();
  const token = `${source}:${reactId}`;

  useEffect(() => {
    if (!active) return;
    const { acquire, release } = useNotificationMuteStore.getState();
    acquire(token);
    return () => release(token);
  }, [active, token]);
}

export function useMediaElementMuteActivity(source: string) {
  const reactId = useId();
  const token = `${source}:${reactId}`;
  const cleanupRef = useRef<(() => void) | null>(null);

  return useCallback(
    (el: HTMLMediaElement | null) => {
      cleanupRef.current?.();
      cleanupRef.current = null;
      if (!el) return;

      const { acquire, release } = useNotificationMuteStore.getState();
      const onActive = () => acquire(token);
      const onInactive = () => release(token);

      el.addEventListener("play", onActive);
      el.addEventListener("playing", onActive);
      el.addEventListener("pause", onInactive);
      el.addEventListener("ended", onInactive);
      el.addEventListener("emptied", onInactive);

      if (!el.paused && !el.ended) acquire(token);

      cleanupRef.current = () => {
        el.removeEventListener("play", onActive);
        el.removeEventListener("playing", onActive);
        el.removeEventListener("pause", onInactive);
        el.removeEventListener("ended", onInactive);
        el.removeEventListener("emptied", onInactive);
        release(token);
      };
    },
    [token],
  );
}
